/**
 * How the menus and the in-match captions look.
 *
 * The target is television, not a game menu: the score bugs, team sheets and lower-third strips
 * a football broadcast puts on the screen. That means a near-black background, one accent
 * colour doing every job, hairlines instead of boxes, a lot of empty space, and the selected
 * row marked by a solid bar of the accent along its left edge rather than by a glow. Nothing
 * moves unless the movement carries information — the one caption a person MUST read pulses,
 * and everything else is still.
 *
 * What was here before was the opposite of all of it: two radial gradients, a diagonal one, a
 * repeating stripe pattern and an eighteen-second drifting animation stacked behind every
 * screen, with bevelled buttons glowing over the top. It competed with the football.
 *
 * NO IMAGE IS ASKED FOR ANYWHERE IN THIS FILE, and that is deliberate. The stylesheet used to
 * name a typeface at a path nothing ships to and two "menu backgrounds" under a directory that
 * does not exist. The art those paths point at, once found, turns out to be the original
 * author's own beta-warning and donation splash — third-party logos and a link asking for money
 * — which is not ours to put on a screen. See `PENDING.md`.
 */
export const shellStyles = `
:root {
  /* Near-black, slightly cool. Not pure black: a real broadcast plate never is. */
  --gf-ink: #06080b;
  --gf-plate: rgb(6 8 11 / 0.92);
  /* Hairlines. The whole layout is built from these rather than from borders round boxes. */
  --gf-line: rgb(255 255 255 / 0.13);
  --gf-line-strong: rgb(255 255 255 / 0.3);
  --gf-text: rgb(255 255 255 / 0.94);
  --gf-dim: rgb(255 255 255 / 0.46);
  /* The one accent. Everything that is live, selected, or a number reads in this. */
  --gf-accent: #ffcf3f;
  --gf-on-accent: #0a0b07;
  /*
   * A condensed grotesque, which is what sports graphics are set in. Every name here is a face
   * that ships with an operating system — DIN Condensed on macOS, Arial Narrow on Windows and
   * macOS, Liberation Sans Narrow on Linux — because this game ships no font of its own.
   */
  --gf-face: "DIN Condensed", "Oswald", "Roboto Condensed", "Arial Narrow",
    "Haettenschweiler", "Liberation Sans Narrow", "Helvetica Neue", system-ui, sans-serif;
}

.gf-shell,
.gf-hud {
  position: fixed;
  inset: 0;
  z-index: 20;
  color: var(--gf-text);
  font-family: var(--gf-face);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.gf-shell {
  display: grid;
  place-items: stretch;
  overflow: hidden;
  background: var(--gf-ink);
}

.gf-shell__viewport {
  position: relative;
  overflow: hidden;
  /*
   * One barely-there wash from the top left, so a full-screen black does not read as a dead
   * page. It is a single flat gradient and it does not move.
   */
  background: linear-gradient(150deg, rgb(255 255 255 / 0.035), transparent 52%);
}

/*
 * Every screen is one column, left-aligned, in a band down the middle of the page. Broadcast
 * graphics hang off a strong left edge; centred text reads as a dialog box.
 */
.gf-screen {
  box-sizing: border-box;
  display: flex;
  width: 100%;
  height: 100%;
  padding: clamp(24px, 6vh, 68px) clamp(20px, 6vw, 90px);
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  gap: clamp(14px, 2.4vh, 26px);
  overflow: auto;
  animation: gf-page-in 140ms ease-out both;
}

.gf-screen > * {
  width: min(100%, 880px);
  margin-inline: auto;
  flex: none;
}

@keyframes gf-page-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* ---------------------------------------------------------------- headings */

/*
 * The strap: a short solid bar of the accent, the small line above the title, and the title.
 * Three elements and no decoration, which is exactly what a lower third is.
 */
.gf-kicker {
  margin: 0 0 6px;
  color: var(--gf-accent);
  font-size: clamp(10px, 1.15vw, 14px);
  letter-spacing: 0.34em;
}

.gf-kicker::before {
  content: "";
  display: block;
  width: 54px;
  height: 3px;
  margin-bottom: 14px;
  background: var(--gf-accent);
}

.gf-title {
  margin: 0;
  padding-bottom: clamp(10px, 1.6vh, 18px);
  border-bottom: 1px solid var(--gf-line-strong);
  color: var(--gf-text);
  font-size: clamp(30px, 5.4vw, 68px);
  line-height: 0.92;
  letter-spacing: 0.015em;
}

/* A title with no kicker still needs the accent bar, or the screen starts on nothing. */
.gf-title:first-child::before {
  content: "";
  display: block;
  width: 54px;
  height: 3px;
  margin-bottom: 16px;
  background: var(--gf-accent);
}

.gf-hint,
.gf-note {
  margin: 0;
  color: var(--gf-dim);
  font-size: clamp(11px, 1.25vw, 15px);
  line-height: 1.5;
  letter-spacing: 0.12em;
  white-space: pre-line;
}

/* The band that names a group of rows: "WHEN YOUR SIDE HAS THE BALL". */
.gf-group {
  margin: clamp(14px, 2.2vh, 24px) 0 0;
  padding-bottom: 5px;
  border-bottom: 1px solid var(--gf-line-strong);
  color: var(--gf-accent);
  font-size: clamp(11px, 1.2vw, 15px);
  letter-spacing: 0.28em;
}

.gf-group:first-child {
  margin-top: 0;
}

/* ----------------------------------------------------------------- buttons */

/*
 * One button in the whole game. It is a full-width row on a hairline; what marks the selected
 * one is a solid bar of the accent along its left edge, which is how a television menu marks a
 * selection and costs nothing to read at a glance.
 *
 * :focus rather than :focus-visible on purpose. Focus IS the cursor here — the shell moves it
 * with the arrow keys and with a gamepad stick — so it has to be visible however it arrived.
 */
.gf-button {
  position: relative;
  box-sizing: border-box;
  display: flex;
  width: 100%;
  min-height: 40px;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 0.5em 0.9em 0.5em 1.1em;
  border: 0;
  border-bottom: 1px solid var(--gf-line);
  color: var(--gf-text);
  background: transparent;
  font: inherit;
  font-size: clamp(13px, 1.6vw, 20px);
  text-transform: uppercase;
  letter-spacing: 0.09em;
  text-align: left;
  cursor: pointer;
  transition: background 110ms linear, color 110ms linear;
}

.gf-button::before {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 4px;
  background: transparent;
  transition: background 110ms linear;
}

.gf-button:hover,
.gf-button:focus,
.gf-button[data-focused] {
  outline: none;
  color: #fff;
  background: rgb(255 255 255 / 0.06);
}

.gf-button:hover::before,
.gf-button:focus::before,
.gf-button[data-focused]::before {
  background: var(--gf-accent);
}

.gf-button:disabled {
  color: rgb(255 255 255 / 0.22);
  cursor: default;
}

/* The one action a screen exists for: filled, so the eye lands on it first. */
.gf-button--primary {
  justify-content: center;
  margin-top: clamp(8px, 1.4vh, 16px);
  border-bottom: 0;
  color: var(--gf-on-accent);
  background: var(--gf-accent);
  letter-spacing: 0.2em;
}

.gf-button--primary:hover,
.gf-button--primary:focus,
.gf-button--primary[data-focused] {
  color: var(--gf-on-accent);
  background: #ffdd74;
}

/*
 * Leaving a match, forfeiting it. Set apart above a rule and lettered dim rather than coloured
 * red: a second colour would undo the one thing this design does.
 */
.gf-button--danger {
  margin-top: clamp(8px, 1.4vh, 16px);
  border-top: 1px solid var(--gf-line);
  color: var(--gf-dim);
}

/* A row of buttons that share a line: the two ways off the full-time screen, ‹ and ›. */
.gf-row {
  display: flex;
  gap: 10px;
}

.gf-row .gf-button {
  width: auto;
  flex: 1;
}

/* ------------------------------------------------------------------- lists */

.gf-stack,
.gf-form {
  display: flex;
  flex-direction: column;
  gap: 0;
}

/* A labelled row: what it is on the left, what it says on the right. */
.gf-field {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(150px, 0.62fr);
  align-items: center;
  gap: 18px;
  min-height: 38px;
  padding: 4px 0.9em 4px 1.1em;
  border-bottom: 1px solid var(--gf-line);
}

.gf-field label {
  color: var(--gf-text);
  font-size: clamp(12px, 1.4vw, 17px);
  letter-spacing: 0.09em;
}

/* A row whose action has no button left on the standard pad — see GAMEPAD_BUTTON_ACTIONS. */
.gf-badge {
  display: inline-block;
  margin-left: 0.7em;
  padding: 0.05em 0.5em;
  border: 1px solid var(--gf-line-strong);
  color: var(--gf-dim);
  font-size: 0.7em;
  letter-spacing: 0.1em;
  vertical-align: middle;
}

.gf-field output {
  color: var(--gf-accent);
  font-size: clamp(12px, 1.4vw, 17px);
  text-align: right;
  font-variant-numeric: tabular-nums;
}

/* A row whose value is itself a button — every key on the controls page is one of these. */
.gf-field .gf-button {
  min-height: 30px;
  padding: 0.2em 0.7em;
  border-bottom: 0;
  color: var(--gf-accent);
  background: rgb(255 255 255 / 0.06);
  justify-content: center;
  text-align: center;
}

.gf-field .gf-button:hover,
.gf-field .gf-button:focus,
.gf-field .gf-button[data-focused] {
  color: var(--gf-on-accent);
  background: var(--gf-accent);
}

.gf-field .gf-button::before {
  width: 3px;
}

.gf-field .gf-button:hover::before,
.gf-field .gf-button:focus::before,
.gf-field .gf-button[data-focused]::before {
  background: transparent;
}

/* The four movement keys, side by side on one row. */
.gf-keys {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 5px;
}

.gf-slider {
  display: flex;
  align-items: center;
  gap: 12px;
}

.gf-field input[type="range"] {
  width: 100%;
  min-width: 0;
  accent-color: var(--gf-accent);
}

.gf-field select {
  min-height: 32px;
  padding: 2px 6px;
  border: 1px solid var(--gf-line-strong);
  color: var(--gf-accent);
  background: var(--gf-ink);
  font: inherit;
  font-size: clamp(12px, 1.35vw, 16px);
  text-transform: uppercase;
}

/* ------------------------------------------------------- the two team sheets */

.gf-columns {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: clamp(16px, 4vw, 48px);
}

.gf-side {
  min-width: 0;
}

.gf-side__title {
  margin: 0 0 10px;
  padding-bottom: 5px;
  border-bottom: 1px solid var(--gf-line-strong);
  color: var(--gf-accent);
  font-size: clamp(11px, 1.2vw, 15px);
  letter-spacing: 0.28em;
}

/*
 * A choice with an arrow either side. The name in the middle is the important thing, so it is
 * set at reading size and the arrows are quiet until one of them is selected.
 */
.gf-carousel {
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr) 34px;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
  border-bottom: 1px solid var(--gf-line);
}

.gf-carousel .gf-button {
  min-height: 34px;
  padding: 0;
  border-bottom: 0;
  color: var(--gf-dim);
  justify-content: center;
}

.gf-carousel .gf-button::before {
  width: 100%;
  height: 3px;
  top: auto;
  bottom: 0;
}

.gf-carousel .gf-button:hover,
.gf-carousel .gf-button:focus,
.gf-carousel .gf-button[data-focused] {
  color: var(--gf-accent);
  background: transparent;
}

.gf-carousel__choice {
  min-width: 0;
  padding: 6px 0;
  color: var(--gf-text);
  font-size: clamp(14px, 1.9vw, 24px);
  line-height: 1.1;
  text-align: center;
  overflow-wrap: anywhere;
}

.gf-carousel__label {
  color: var(--gf-dim);
  font-size: clamp(10px, 1.05vw, 13px);
  letter-spacing: 0.26em;
}

/* --------------------------------------------------------------- loading */

.gf-loading {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  gap: clamp(12px, 3vw, 32px);
  padding-top: clamp(10px, 2vh, 22px);
}

.gf-loading__team {
  font-size: clamp(18px, 3vw, 40px);
  line-height: 1;
}

.gf-loading__team:last-child {
  text-align: right;
}

.gf-spinner {
  width: 34px;
  height: 34px;
  border: 3px solid rgb(255 255 255 / 0.16);
  border-top-color: var(--gf-accent);
  border-radius: 50%;
  animation: gf-spin 900ms linear infinite;
}

@keyframes gf-spin { to { transform: rotate(360deg); } }

/* --------------------------------------------------------------- credits */

.gf-credits {
  display: grid;
  gap: 2px;
  max-height: 52vh;
  overflow: auto;
  color: var(--gf-dim);
  font-size: clamp(12px, 1.4vw, 17px);
  line-height: 1.7;
  letter-spacing: 0.14em;
}

.gf-credits__name {
  color: var(--gf-text);
}

/* =========================================================== in-match =========== */

.gf-hud {
  z-index: 10;
  pointer-events: none;
}

/*
 * The score bug. A block of accent carrying the clock, then the two sides and the score on a
 * dark plate — the shape every broadcast uses, and small enough to be ignored.
 */
.gf-scoreboard {
  position: absolute;
  top: clamp(14px, 2.4vh, 28px);
  left: clamp(14px, 2vw, 30px);
  display: flex;
  align-items: stretch;
  height: 32px;
  background: var(--gf-plate);
  box-shadow: 0 2px 14px rgb(0 0 0 / 0.55);
  font-size: 16px;
  letter-spacing: 0.1em;
  font-variant-numeric: tabular-nums;
}

.gf-scoreboard__clock {
  display: flex;
  align-items: center;
  padding: 0 10px;
  color: var(--gf-on-accent);
  background: var(--gf-accent);
}

.gf-scoreboard span:last-child {
  display: flex;
  align-items: center;
  padding: 0 12px;
  white-space: nowrap;
}

.gf-pause-layer {
  position: fixed;
  inset: 0;
  z-index: 30;
  display: grid;
  place-items: center;
  pointer-events: auto;
  background: rgb(3 4 6 / 0.82);
}

.gf-pause-layer[hidden],
.gf-result-layer[hidden],
.gf-interval[hidden],
.gf-restart[hidden],
.gf-card[hidden],
.gf-goal[hidden],
.gf-learning[hidden],
.gf-audio-gate[hidden],
.gf-audio-hint[hidden],
.gf-shell[hidden],
.gf-hud[hidden] {
  display: none;
}

.gf-pause-panel {
  box-sizing: border-box;
  width: min(560px, 88vw);
  max-height: 84vh;
  padding: clamp(18px, 3vh, 30px) clamp(16px, 3vw, 30px);
  overflow: auto;
  border-left: 3px solid var(--gf-accent);
  background: var(--gf-plate);
}

/*
 * Learning mode: what the button you just pressed actually did.
 *
 * Deliberately the opposite of the restart caption below. That one MUST be read and sits over
 * the middle of the pitch; this one is a hint somebody should be able to leave switched on and
 * stop noticing, so it is small, low, dim and does not pulse. It still gets a plate, because
 * every caption in this game sooner or later ends up over the stand.
 */
.gf-learning {
  position: absolute;
  bottom: 6%;
  left: 0;
  right: 0;
  display: grid;
  justify-items: center;
  pointer-events: none;
}

.gf-learning__plate {
  padding: 4px 14px;
  border-left: 3px solid var(--gf-accent);
  background: var(--gf-plate);
  color: rgb(255 255 255 / 0.82);
  font-size: clamp(11px, 1.4vw, 16px);
  letter-spacing: 0.14em;
  text-align: center;
  animation: gf-learning-in 140ms ease-out both;
}

@keyframes gf-learning-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

/*
 * "The match is waiting for you, press this."
 *
 * This was a small line along the bottom edge first, which was a mistake: it is the one piece
 * of text in the game that MUST be read, and somebody who has just kicked off is looking at
 * the middle of the pitch. It sits above the action on a solid plate. The plate is not
 * decoration — white lettering over a stand full of pale shirts is genuinely unreadable — and
 * the slow pulse is the one animation in this file that carries information.
 */
.gf-restart {
  position: absolute;
  top: 20%;
  left: 0;
  right: 0;
  display: grid;
  justify-items: center;
  pointer-events: none;
}

.gf-restart__plate {
  padding: clamp(6px, 1.2vh, 12px) clamp(16px, 3.5vw, 40px);
  border-left: 4px solid var(--gf-accent);
  background: var(--gf-plate);
  box-shadow: 0 8px 34px rgb(0 0 0 / 0.7);
  color: var(--gf-accent);
  font-size: clamp(15px, 2.4vw, 30px);
  letter-spacing: 0.1em;
  text-align: center;
  animation: gf-restart-pulse 1.6s ease-in-out infinite;
}

@keyframes gf-restart-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.62; }
}

/*
 * The card graphic. Sits above the restart caption's 20% rather than sharing it, because a
 * card ceremony holds the ball up before the restart it delays ever counts as a live set
 * piece — the two do not actually overlap — but stacking them by position rather than by luck
 * keeps it that way if the timing ever changes.
 */
.gf-card {
  position: absolute;
  top: 9%;
  left: 0;
  right: 0;
  display: grid;
  justify-items: center;
  pointer-events: none;
  animation: gf-page-in 200ms ease-out both;
}

.gf-card__plate {
  padding: clamp(6px, 1.2vh, 12px) clamp(16px, 3.5vw, 40px);
  border-left: 4px solid var(--gf-accent);
  background: var(--gf-plate);
  box-shadow: 0 8px 34px rgb(0 0 0 / 0.7);
  color: var(--gf-accent);
  font-size: clamp(13px, 1.8vw, 22px);
  letter-spacing: 0.1em;
  text-align: center;
}

/* A second yellow or a straight red reads red rather than the ordinary gold accent. */
.gf-card--red .gf-card__plate {
  border-left-color: #ff4d4d;
  color: #ff4d4d;
}

/*
 * The goal banner. Same plate as the card graphic above — same border, plate, shadow and
 * type — because it is the same kind of announcement, just for a bigger moment. It cannot
 * share the card's own 9% band, though: a goal has its restart already being prepared the
 * instant it is given, so the restart caption at 20% is routinely on screen at the same time
 * as this one, unlike the card ceremony, which is over before its restart ever shows. Sitting
 * well clear of both.
 */
.gf-goal {
  position: absolute;
  top: 30%;
  left: 0;
  right: 0;
  display: grid;
  justify-items: center;
  pointer-events: none;
  animation: gf-page-in 200ms ease-out both;
}

.gf-goal__plate {
  padding: clamp(6px, 1.2vh, 12px) clamp(16px, 3.5vw, 40px);
  border-left: 4px solid var(--gf-accent);
  background: var(--gf-plate);
  box-shadow: 0 8px 34px rgb(0 0 0 / 0.7);
  color: var(--gf-accent);
  font-size: clamp(13px, 1.8vw, 22px);
  letter-spacing: 0.1em;
  text-align: center;
}

/* The interval caption: television puts the score on the screen and gets out of the way. */
.gf-interval {
  position: absolute;
  top: 15%;
  left: 0;
  right: 0;
  display: grid;
  justify-items: center;
  pointer-events: none;
  animation: gf-page-in 300ms ease-out both;
}

.gf-interval__plate {
  padding: clamp(8px, 1.4vh, 16px) clamp(20px, 4.5vw, 56px);
  border-left: 4px solid var(--gf-accent);
  background: var(--gf-plate);
  box-shadow: 0 8px 34px rgb(0 0 0 / 0.7);
}

.gf-interval__title {
  margin: 0;
  font-size: clamp(24px, 4.4vw, 56px);
  line-height: 0.95;
  letter-spacing: 0.04em;
}

.gf-interval__score {
  margin: 6px 0 0;
  color: var(--gf-dim);
  font-size: clamp(12px, 1.7vw, 21px);
  letter-spacing: 0.14em;
}

/* ------------------------------------------------------------- full time */

.gf-result-layer {
  position: fixed;
  inset: 0;
  z-index: 40;
  display: grid;
  place-items: center;
  pointer-events: auto;
  background: rgb(3 4 6 / 0.9);
  animation: gf-page-in 600ms ease-out both;
}

.gf-result {
  box-sizing: border-box;
  width: min(720px, 92vw);
  max-height: 92vh;
  padding: clamp(18px, 3.4vh, 36px) clamp(18px, 4vw, 44px);
  overflow: auto;
  border-left: 4px solid var(--gf-accent);
  background: var(--gf-plate);
}

.gf-result__score {
  display: grid;
  align-items: center;
  margin: clamp(8px, 1.6vh, 16px) 0 0;
  padding-bottom: clamp(8px, 1.6vh, 16px);
  border-bottom: 1px solid var(--gf-line-strong);
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  gap: clamp(10px, 2.4vw, 28px);
}

.gf-result__team {
  font-size: clamp(13px, 1.9vw, 24px);
  line-height: 1.05;
}

.gf-result__team:last-child {
  text-align: right;
}

.gf-result__goals {
  color: var(--gf-accent);
  font-size: clamp(34px, 6vw, 74px);
  line-height: 0.85;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.gf-result__verdict {
  margin: clamp(8px, 1.6vh, 16px) 0 0;
  color: var(--gf-dim);
  font-size: clamp(12px, 1.5vw, 18px);
  letter-spacing: 0.24em;
}

.gf-result__stats {
  display: grid;
  gap: clamp(8px, 1.4vh, 15px);
  margin: clamp(12px, 2.2vh, 24px) 0;
}

.gf-stat {
  display: grid;
  align-items: baseline;
  grid-template-columns: 4em minmax(0, 1fr) 4em;
  gap: 10px;
}

.gf-stat__value {
  color: var(--gf-text);
  font-size: clamp(14px, 1.8vw, 22px);
  font-variant-numeric: tabular-nums;
}

.gf-stat__value--away {
  text-align: right;
}

.gf-stat__label {
  color: var(--gf-dim);
  font-size: clamp(10px, 1.15vw, 14px);
  letter-spacing: 0.2em;
  text-align: center;
}

.gf-stat__bar {
  display: flex;
  grid-column: 1 / -1;
  height: 3px;
  margin-top: -3px;
  background: rgb(255 255 255 / 0.1);
}

.gf-stat__bar span:first-child {
  background: var(--gf-accent);
}

.gf-stat__bar span:last-child {
  background: rgb(255 255 255 / 0.34);
}

/* ------------------------------------------------------------- narrow screens */

@media (max-width: 620px), (max-aspect-ratio: 4 / 5) {
  .gf-screen {
    justify-content: flex-start;
  }
  .gf-columns {
    grid-template-columns: 1fr;
  }
  .gf-field {
    grid-template-columns: 1fr;
    gap: 2px;
  }
  .gf-field output {
    text-align: left;
  }
  .gf-row {
    flex-direction: column;
  }
}

/*
 * The boot gate. Nothing else on the page can be reached until this closes, so it sits above
 * every other layer (the pause menu tops out at z-index 30) and dims the whole viewport rather
 * than floating over it like a caption.
 */
.gf-audio-gate {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: grid;
  place-items: center;
  pointer-events: auto;
  background: rgb(3 4 6 / 0.92);
}

.gf-audio-gate__plate {
  box-sizing: border-box;
  max-width: min(560px, 88vw);
  padding: clamp(18px, 3vh, 30px) clamp(20px, 4vw, 36px);
  display: grid;
  gap: 10px;
  justify-items: center;
  border-left: 3px solid var(--gf-accent);
  background: var(--gf-plate);
  box-shadow: 0 8px 34px rgb(0 0 0 / 0.7);
  text-align: center;
  animation: gf-restart-pulse 1.6s ease-in-out infinite;
}

.gf-audio-gate__title {
  color: var(--gf-accent);
  font-size: clamp(15px, 2.4vw, 26px);
  letter-spacing: 0.1em;
}

.gf-audio-gate__note {
  color: rgb(255 255 255 / 0.6);
  font-size: clamp(10px, 1.2vw, 14px);
  letter-spacing: 0.06em;
}

/*
 * The fallback for the one case the gate cannot actually close: a gamepad button dismisses it
 * (nobody is left staring at a screen their only input device cannot pass), but a browser will
 * not treat that as the gesture its audio-unlock policy wants, so this says so until a real one
 * — any key or click — eventually arrives, same as .gf-learning's register.
 */
.gf-audio-hint {
  position: absolute;
  bottom: 6%;
  left: 0;
  right: 0;
  display: grid;
  justify-items: center;
  pointer-events: none;
}

.gf-audio-hint__plate {
  padding: 4px 14px;
  border-left: 3px solid var(--gf-accent);
  background: var(--gf-plate);
  color: rgb(255 255 255 / 0.82);
  font-size: clamp(11px, 1.4vw, 16px);
  letter-spacing: 0.14em;
  text-align: center;
  animation: gf-learning-in 140ms ease-out both;
}

@media (prefers-reduced-motion: reduce) {
  .gf-screen,
  .gf-spinner,
  .gf-interval,
  .gf-result-layer,
  .gf-learning__plate,
  .gf-restart__plate,
  .gf-card,
  .gf-goal,
  .gf-audio-gate__plate,
  .gf-audio-hint__plate {
    animation: none;
  }
}
`;
